import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import {
  COUNTRY_PHONE_OPTIONS,
  DEFAULT_COUNTRY,
  CountryPhone,
} from '../constants/signup.constants';

interface IpApiResponse {
  ip: string;
  country_code: string;
  country_name: string;
  city: string;
  region: string;
}

interface GeoLocationData {
  countryCode: string;
  countryName: string;
  city: string;
  ip: string;
  detectedCountry: CountryPhone;
}

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  // ipapi.co funciona mejor y soporta HTTPS
  private readonly API_URL = 'https://ipapi.co/json/';

  // Cache del resultado
  private geoData = new BehaviorSubject<GeoLocationData | null>(null);
  private isLoading = new BehaviorSubject<boolean>(false);
  private hasLoaded = false;

  // Mapeo de códigos de país ISO a nuestros países
  private readonly countryCodeMap: Record<string, string> = {
    'CL': 'CHL', // Chile
    'VE': 'VEN', // Venezuela
    'AR': 'ARG', // Argentina
    'BO': 'BOL', // Bolivia
    'BR': 'BRA', // Brasil
    'CO': 'COL', // Colombia
    'EC': 'ECU', // Ecuador
    'PY': 'PRY', // Paraguay
    'PE': 'PER', // Perú
    'UY': 'URY', // Uruguay
    'US': 'USA', // Estados Unidos
  };

  constructor(private http: HttpClient) {}

  /**
   * Obtiene la geolocalización por IP
   * Cachea el resultado para no hacer múltiples llamadas
   */
  getGeolocation(): Observable<GeoLocationData | null> {
    // Si ya cargó, devolver el valor cacheado
    if (this.hasLoaded) {
      return this.geoData.asObservable();
    }

    // Si está cargando, esperar
    if (this.isLoading.value) {
      return this.geoData.asObservable();
    }

    this.isLoading.next(true);

    // Header 'skip' para omitir el interceptor de autenticación
    const headers = new HttpHeaders().set('skip', 'true');

    return this.http.get<IpApiResponse>(this.API_URL, { headers }).pipe(
      timeout(5000), // Timeout de 5 segundos
      map((response) => {
        if (!response.country_code) {
          return this.getDefaultGeoData();
        }

        const isoCode = this.countryCodeMap[response.country_code] || null;
        const detectedCountry = isoCode
          ? COUNTRY_PHONE_OPTIONS.find((c) => c.iso === isoCode) || DEFAULT_COUNTRY
          : DEFAULT_COUNTRY;

        const geoData: GeoLocationData = {
          countryCode: response.country_code,
          countryName: response.country_name,
          city: response.city || '',
          ip: response.ip,
          detectedCountry,
        };

        return geoData;
      }),
      tap((data) => {
        this.geoData.next(data);
        this.hasLoaded = true;
        this.isLoading.next(false);
      }),
      catchError((error) => {
        const defaultData = this.getDefaultGeoData();
        this.geoData.next(defaultData);
        this.hasLoaded = true;
        this.isLoading.next(false);
        return of(defaultData);
      })
    );
  }

  /**
   * Obtiene el país detectado (sync) - usar después de llamar getGeolocation()
   */
  get detectedCountry(): CountryPhone {
    return this.geoData.value?.detectedCountry || DEFAULT_COUNTRY;
  }

  /**
   * Obtiene los datos de geo cacheados
   */
  get cachedGeoData(): GeoLocationData | null {
    return this.geoData.value;
  }

  /**
   * Verifica si el país detectado es Chile
   */
  get isChile(): boolean {
    return this.detectedCountry.iso === 'CHL';
  }

  /**
   * Verifica si el país detectado es Venezuela
   */
  get isVenezuela(): boolean {
    return this.detectedCountry.iso === 'VEN';
  }

  /**
   * Verifica si el país detectado está en nuestra lista
   */
  get isKnownCountry(): boolean {
    const iso = this.geoData.value?.detectedCountry?.iso;
    return COUNTRY_PHONE_OPTIONS.some((c) => c.iso === iso);
  }

  /**
   * Obtiene el tipo de documento/OCR a usar según el país
   */
  get documentType(): 'chile' | 'venezuela' | 'other' {
    if (this.isChile) return 'chile';
    if (this.isVenezuela) return 'venezuela';
    return 'other';
  }

  /**
   * Reinicia el cache (útil para testing o cambio de contexto)
   */
  resetCache(): void {
    this.geoData.next(null);
    this.hasLoaded = false;
  }

  private getDefaultGeoData(): GeoLocationData {
    return {
      countryCode: 'CL',
      countryName: 'Chile',
      city: '',
      ip: '',
      detectedCountry: DEFAULT_COUNTRY,
    };
  }
}
